import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Drop, Plus, Minus, Trash } from '@phosphor-icons/react';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { useConfirmDelete } from '@/components/ConfirmDialogs';
import { fmtDate as fmtDatePt } from '@/utils/dateFmt';

const API = process.env.REACT_APP_BACKEND_URL;

export default function AguaPage() {
  const confirmDelete = useConfirmDelete();
  const [glasses, setGlasses] = useState(0);
  const [goal, setGoal] = useState(8); // default 8 glasses (2L)
  const [loading, setLoading] = useState(true);
  const [records, setRecords] = useState([]);
  const [showAdd, setShowAdd] = useState(false);
  const [form, setForm] = useState({ date: new Date().toISOString().split('T')[0], amount_ml: '', notes: '' });

  const fetchRecords = useCallback(async () => {
    try { const { data } = await axios.get(`${API}/api/my-hydration`); setRecords(data || []); } catch { /* noop */ }
  }, []);

  useEffect(() => {
    (async () => {
      try {
        const [logRes, configRes] = await Promise.all([
          axios.get(`${API}/api/alerts/water-log`),
          axios.get(`${API}/api/auth/me`),
        ]);
        setGlasses(logRes.data?.glasses || 0);
        // Goal from config or default
        const userGoal = configRes.data?.water_goal_glasses;
        if (userGoal) setGoal(userGoal);
      } catch {}
      finally { setLoading(false); }
    })();
    fetchRecords();
  }, [fetchRecords]);

  const addGlass = async () => {
    try { const { data } = await axios.post(`${API}/api/alerts/water-log`, { action: 'add' }); setGlasses(data.glasses); } catch {}
  };
  const removeGlass = async () => {
    if (glasses <= 0) return;
    try { const { data } = await axios.post(`${API}/api/alerts/water-log`, { action: 'remove' }); setGlasses(data.glasses); } catch {}
  };

  const handleAddRecord = async () => {
    if (!form.amount_ml) return;
    try {
      await axios.post(`${API}/api/my-hydration`, { date: form.date, amount_ml: parseInt(form.amount_ml, 10), notes: form.notes });
      toast.success('Hidratação registada');
      setShowAdd(false); setForm({ date: new Date().toISOString().split('T')[0], amount_ml: '', notes: '' }); fetchRecords();
    } catch { toast.error('Erro ao guardar'); }
  };
  const delRecord = async (id) => {
    const r = records.find(x => x.id === id);
    const ok = await confirmDelete({ description: `Vai apagar o registo de hidratação de ${r?.date || ''}.` });
    if (!ok) return;
    try { await axios.delete(`${API}/api/my-hydration/${id}`); toast.success('Registo eliminado'); fetchRecords(); } catch { /* noop */ }
  };

  const progress = Math.min(100, (glasses / goal) * 100);
  const ml = glasses * 250;
  const goalMl = goal * 250;

  if (loading) return <div className="text-center py-12 text-brand-muted">A carregar...</div>;

  return (
    <div className="fade-in" data-testid="agua-page">
      <div className="text-center mb-6">
        <h2 className="font-heading font-medium text-xl text-brand-text">Hidratação</h2>
        <p className="text-xs text-brand-muted mt-1">Regista a tua ingestão de água hoje</p>
      </div>

      {/* Progress circle */}
      <div className="flex justify-center mb-6">
        <div className="relative w-48 h-48">
          <svg className="w-full h-full transform -rotate-90" viewBox="0 0 100 100">
            <circle cx="50" cy="50" r="42" fill="none" stroke="#E8EBE6" strokeWidth="8" />
            <circle cx="50" cy="50" r="42" fill="none" stroke="#657161" strokeWidth="8"
              strokeDasharray={`${progress * 2.64} 264`} strokeLinecap="round"
              className="transition-all duration-500" />
          </svg>
          <div className="absolute inset-0 flex flex-col items-center justify-center">
            <Drop size={24} className="text-brand-green mb-1" weight="fill" />
            <p className="text-3xl font-heading font-bold text-brand-text">{ml}</p>
            <p className="text-xs text-brand-muted">de {goalMl} ml</p>
          </div>
        </div>
      </div>

      {/* Glasses counter */}
      <div className="bg-white border border-brand-border rounded-2xl p-5 text-center mb-4">
        <p className="text-xs text-brand-muted mb-3">{glasses} de {goal} copos (250ml cada)</p>
        <div className="flex items-center justify-center gap-6">
          <button onClick={removeGlass} className="w-12 h-12 rounded-full border border-brand-border flex items-center justify-center text-brand-muted hover:bg-brand-bg" data-testid="water-minus">
            <Minus size={20} />
          </button>
          <span className="text-4xl font-heading font-bold text-brand-green">{glasses}</span>
          <button onClick={addGlass} className="w-12 h-12 rounded-full bg-brand-green text-white flex items-center justify-center shadow-md hover:bg-brand-green-hover" data-testid="water-plus">
            <Plus size={20} />
          </button>
        </div>
      </div>

      {/* Goal status */}
      {glasses >= goal ? (
        <div className="bg-brand-mint/20 border border-brand-green/20 rounded-2xl p-4 text-center mb-6">
          <p className="text-sm font-medium text-brand-green">Meta atingida! Parabéns pela hidratação de hoje.</p>
        </div>
      ) : (
        <p className="text-center text-xs text-brand-muted mb-6">Faltam {goal - glasses} copos para atingires a meta de hoje.</p>
      )}

      {/* Histórico diário (registo manual com notas, visível à nutricionista) */}
      <div className="flex items-center justify-between mb-3">
        <h3 className="font-heading font-medium text-xs text-brand-muted uppercase tracking-wider">Histórico diário</h3>
        <button onClick={() => setShowAdd(true)} className="bg-brand-green text-white rounded-full px-3 py-1.5 text-xs font-medium flex items-center gap-1" data-testid="add-hydration-btn">
          <Plus size={12} /> Registar dia
        </button>
      </div>
      {records.length === 0 ? (
        <div className="text-center py-8 bg-white border border-brand-border rounded-2xl" data-testid="hydration-history-empty">
          <p className="text-xs text-brand-muted">Sem registos diários de hidratação.</p>
        </div>
      ) : (
        <div className="space-y-2">
          {records.map(r => (
            <div key={r.id} className="bg-white border border-brand-border rounded-2xl p-3 flex items-center justify-between" data-testid={`hydration-record-${r.id}`}>
              <div>
                <span className="text-xs text-brand-muted">{fmtDatePt(r.date)}</span>
                {r.notes && <p className="text-[10px] text-brand-muted italic">"{r.notes}"</p>}
              </div>
              <div className="flex items-center gap-3">
                <p className="text-sm font-heading font-semibold text-brand-text">{(r.amount_ml || 0).toLocaleString('pt-PT')} ml</p>
                <button onClick={() => delRecord(r.id)} className="p-1 text-brand-placeholder hover:text-red-500" data-testid={`delete-hydration-${r.id}`}><Trash size={13} /></button>
              </div>
            </div>
          ))}
        </div>
      )}

      <Dialog open={showAdd} onOpenChange={setShowAdd}>
        <DialogContent className="rounded-3xl border-brand-border" data-testid="hydration-add-dialog">
          <DialogHeader><DialogTitle className="font-heading text-brand-text">Registar hidratação do dia</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label className="text-xs text-brand-muted mb-1 block">Data</Label><Input type="date" value={form.date} onChange={e => setForm({ ...form, date: e.target.value })} className="rounded-xl border-brand-border" data-testid="hydration-date" /></div>
            <div><Label className="text-xs text-brand-muted mb-1 block">Quantidade (ml)</Label><Input type="number" step="50" value={form.amount_ml} onChange={e => setForm({ ...form, amount_ml: e.target.value })} placeholder="Ex: 2000" className="rounded-xl border-brand-border" data-testid="hydration-amount" /></div>
            <div><Label className="text-xs text-brand-muted mb-1 block">Notas (opcional)</Label><Textarea value={form.notes} onChange={e => setForm({ ...form, notes: e.target.value })} className="rounded-xl border-brand-border text-sm" placeholder="Ex: dia mais calor, treino, etc." data-testid="hydration-notes" /></div>
            <button onClick={handleAddRecord} className="w-full bg-brand-green text-white rounded-full py-3 font-medium" data-testid="save-hydration-btn">Guardar</button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
